/**
 * awe-cart-progress.js
 *
 * Custom element that manages the gift auto-add/remove logic for the Goddess
 * Circle cart progress bar. All display logic (fill width, message, marker
 * states) is server-rendered by Liquid; this element only handles cart API
 * calls for the gift milestone.
 *
 * Architecture:
 *   - connectedCallback fires when element is freshly inserted (initial render)
 *   - attributeChangedCallback fires when morphdom patches attributes in-place
 *     (e.g. user removes an item → Horizon morphs the section → attributes
 *      change but connectedCallback does NOT re-fire)
 *   - Both paths call #checkAndSyncGift(), which reads the live cart (/cart.js)
 *     — the data attributes only supply the threshold and gift variant
 *   - If gift threshold is met and gift not in cart → add gift silently
 *   - If gift threshold is not met and gift is in cart → remove gift
 *   - After any API change → dispatch cart:update → Horizon re-renders section
 *   - Module-level flag prevents concurrent runs across back-to-back callbacks;
 *     a check skipped while an operation is in flight is re-run once it finishes
 *     (otherwise a subtotal drop during an add — e.g. the Goddess Circle line
 *     arriving and Member-10 applying — left the gift in a cart below the threshold)
 *   - Also re-checks shortly after every cart:update, so any later price change
 *     (Member-10, VIP 30%, quantity edits elsewhere) adds/removes the gift
 *   - AbortController cleans up listeners in disconnectedCallback
 *
 * Data attributes (all set by awe-cart-progress.liquid):
 *   data-gift-enabled     'true' | 'false'
 *   data-gift-variant-id  Shopify variant ID of the gift product
 *   data-gift-threshold   Threshold in cents (integer)
 *   data-cart-subtotal    Cart subtotal in cents excluding gift items
 *   data-has-gift         'true' | 'false' — gift currently in cart
 *   data-gift-item-key    Shopify line item key of the gift (for removal)
 */

/** @type {boolean} Prevents concurrent gift add/remove operations. */
let giftOpInProgress = false;

/** @type {boolean} A check was skipped while an operation was in flight — re-run it after. */
let giftCheckPending = false;

/** Latest cart seen (own fetches + cart:update events), for AweCartGift.updatesFor. */
let latestCart = null;

/** Gift threshold in cents, from the rendered component. */
let giftThresholdCents = 0;

/** @param {any} item */
const isGiftLine = (item) => item.properties?._gift === 'true';

/** Lines that count toward the gift threshold (mirrors awe-cart-progress.liquid). */
const countsTowardGift = (item) =>
  !isGiftLine(item) && item.properties?._awemembership !== 'true' && item.vendor !== 'Order Protection';

/**
 * Lets Horizon's quantity change / Remove (component-cart-items.js) drop the
 * free gift in the SAME request when the change takes the cart below the
 * threshold. Before, the gift went in a second request after the quantity
 * change returned, so it sat in the cart — at full price, since the FREE GIFT
 * discount had already stopped — for 3–7s and could reach checkout.
 *
 * The estimate scales the changed line's current price to the new quantity.
 * If it's ever off right at the threshold (member / VIP rounding), the normal
 * check after the update re-adds or removes the gift as before.
 */
window.AweCartGift = {
  /**
   * @param {number} line - 1-based cart line being changed (Horizon's line number)
   * @param {number} quantity - its new quantity (0 = remove)
   * @returns {Record<string, number> | null} /cart/update.js `updates` (line + gift → 0), or null to change the line alone
   */
  updatesFor(line, quantity) {
    const items = latestCart?.items;
    if (!Array.isArray(items) || !giftThresholdCents) return null;

    const target = items[line - 1];
    const giftLines = items.filter(isGiftLine);
    if (!target || !giftLines.length || isGiftLine(target)) return null;

    let subtotal = items.filter(countsTowardGift).reduce((sum, item) => sum + item.final_line_price, 0);
    if (countsTowardGift(target) && target.quantity > 0) {
      subtotal += Math.round((target.final_line_price / target.quantity) * quantity) - target.final_line_price;
    }
    if (subtotal >= giftThresholdCents) return null;

    /** @type {Record<string, number>} */
    const updates = { [target.key]: quantity };
    for (const gift of giftLines) updates[gift.key] = 0;
    return updates;
  },
};

class AweCartProgressComponent extends HTMLElement {
  /** @type {AbortController|null} */
  #controller = null;

  /**
   * Observe the dynamic attributes that Liquid re-bakes on every section
   * re-render. attributeChangedCallback fires when morphdom patches these
   * in-place (without removing/re-inserting the element), covering the case
   * where connectedCallback does NOT re-fire.
   */
  static observedAttributes = [
    'data-cart-subtotal',
    'data-has-gift',
    'data-gift-item-key',
    'data-gift-enabled',
  ];

  connectedCallback() {
    this.#controller = new AbortController();
    giftThresholdCents = Number(this.dataset.giftThreshold || 0);
    this.#checkAndSyncGift();

    // Re-check after any cart change (Horizon re-render, membership toggle, other
    // features). When the event carries the updated cart (Horizon's quantity
    // change / remove, Shop the Look), decide from it right away — no wait and no
    // extra /cart.js — so a gift that no longer qualifies goes ~1s sooner and
    // shoppers can't reach checkout with it in the meantime. Events without a
    // cart keep the delayed re-check, after the section render has landed.
    let recheckTimer = null;
    document.addEventListener(
      'cart:update',
      (event) => {
        clearTimeout(recheckTimer);
        const cart = /** @type {CustomEvent} */ (event).detail?.resource;
        if (Array.isArray(cart?.items) && typeof cart.item_count === 'number') {
          latestCart = cart;
          this.#checkAndSyncGift(cart);
        } else {
          recheckTimer = setTimeout(() => this.#checkAndSyncGift(), 1000);
        }
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Called whenever an observed attribute changes (covers morphdom in-place
   * patches where connectedCallback does not re-fire).
   */
  attributeChangedCallback() {
    // Guard: don't run before the element is in the document.
    if (!this.isConnected) return;
    this.#checkAndSyncGift();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Core gift sync logic
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Decides from the LIVE cart (/cart.js), not the data attributes — those can be
   * stale while a section re-render is pending, which could re-add an existing
   * gift. Subtotal mirrors awe-cart-progress.liquid: final_line_price of every
   * line except the gift, the membership line and Order Protection.
   * One check/operation at a time (module flag); a check requested meanwhile is
   * queued and re-run afterwards (from a fresh /cart.js).
   * @param {{ items: Array<any> }} [knownCart] - cart just returned by the server, used instead of fetching
   */
  async #checkAndSyncGift(knownCart) {
    if (this.dataset.giftEnabled !== 'true') return;

    const giftThreshold = Number(this.dataset.giftThreshold || 0);
    const giftVariantId = Number(this.dataset.giftVariantId || 0);
    if (!giftThreshold || !giftVariantId) return;

    if (giftOpInProgress) {
      giftCheckPending = true;
      return;
    }
    giftOpInProgress = true;

    let changed = false;
    try {
      const cart = knownCart ?? (await this.#fetchCart());
      if (!cart) return;
      latestCart = cart;

      const items = cart.items || [];
      const giftLines = items.filter(isGiftLine);
      const subtotal = items.filter(countsTowardGift).reduce((sum, item) => sum + item.final_line_price, 0);
      const meetsThreshold = subtotal >= giftThreshold;

      if (meetsThreshold && !giftLines.length) {
        changed = await this.#addGift(giftVariantId);
      } else if (!meetsThreshold && giftLines.length) {
        for (const line of giftLines) changed = (await this.#setGiftQuantity(line.key, 0)) || changed;
      } else if (meetsThreshold) {
        // Exactly one gift: drop extra lines / quantities (e.g. a double add)
        for (const [index, line] of giftLines.entries()) {
          const wanted = index === 0 ? 1 : 0;
          if (line.quantity !== wanted) changed = (await this.#setGiftQuantity(line.key, wanted)) || changed;
        }
      }
    } catch (err) {
      console.error('[awe-progress] Gift sync error:', err);
    } finally {
      giftOpInProgress = false;
      if (changed) this.#triggerCartUpdate();
      this.#runPendingCheck();
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Gift API calls
  // ────────────────────────────────────────────────────────────────────────────

  async #fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      console.warn('[awe-progress] /cart.js failed:', response.status);
      return null;
    }
    return response.json();
  }

  /**
   * @param {number} variantId
   * @returns {Promise<boolean>} whether the cart changed
   */
  async #addGift(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            _gift: 'true',          // Hidden identifier used by Liquid to exclude from subtotal
            'Free Gift': 'Included', // Visible label shown on cart line item
          },
        }],
      }),
    });

    if (!response.ok) {
      // Out-of-stock or other Shopify error — log and bail silently.
      console.warn('[awe-progress] Gift add failed:', response.status, await response.text());
      return false;
    }
    return true;
  }

  /**
   * @param {string} itemKey
   * @param {number} quantity - 0 removes the line
   * @returns {Promise<boolean>} whether the cart changed
   */
  async #setGiftQuantity(itemKey, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity }),
    });

    if (!response.ok) {
      console.warn('[awe-progress] Gift update failed:', response.status, await response.text());
      return false;
    }
    return true;
  }

  /**
   * Re-runs a check that was requested while an operation was in flight.
   */
  #runPendingCheck() {
    if (!giftCheckPending) return;
    giftCheckPending = false;
    setTimeout(() => {
      if (this.isConnected) this.#checkAndSyncGift();
    }, 300);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Horizon cart re-render
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Dispatches cart:update to trigger Horizon's CartItemsComponent re-render.
   *
   * detail.data.sections is undefined (falsy) → Horizon falls through to
   * sectionRenderer.renderSection(sectionId, { cache: false }) which fetches
   * fresh section HTML and morphs the DOM.
   *
   * Deferred with setTimeout so the dispatch lands after any in-flight
   * morphdom / sectionRenderer cycle has fully completed, preventing the
   * event from being silently swallowed by a concurrent render guard.
   */
  #triggerCartUpdate() {
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: {} },
        })
      );
    }, 0);
  }
}

if (!customElements.get('awe-cart-progress-component')) {
  customElements.define('awe-cart-progress-component', AweCartProgressComponent);
}
